import type { CompanionScope } from "@/types/enums/CompanionScope";
import { Guest } from "@/types/Guest";
import { DuplicatesResponse } from "@/types/responses/DuplicateResponse";
import { apiDelete, apiGet, apiPut, apiPost } from "../_apiShim";
import { Filter } from "@/types/Filter";
import { ConditionGroup } from "@/types/EventRule";
import { EmailOptInBreakdown } from "@/types/EmailOptInBreakdown";
import { ImportBatch } from "@/types/ImportBatch";
import { readDeleteGuestsResult, type DeleteGuestsResult } from "@/types/GuestTrash";
import { withoutNonClearable } from "@/lib/utils/guestClearFields";

const BASE_PATH = "/api/v1/guests/";

// Parse a response that may be a JSON string of IDs or an array
function parseGuestBatchResponse(response: unknown): string[] {
  if (typeof response === "string") {
    try {
      if (response.startsWith("[") && response.endsWith("]")) {
        const parsed = JSON.parse(response);
        if (Array.isArray(parsed)) {
          return parsed;
        }
        const trimmed = response.substring(1, response.length - 1);
        return trimmed.split(",").map((id) => id.trim().replace(/"/g, ""));
      }
      return [response.replace(/"/g, "").replace(/[\[\]]/g, "")];
    } catch {
      return [response.replace(/"/g, "").replace(/[\[\]]/g, "")];
    }
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (typeof response === "object" && response !== null && !Array.isArray(response)) {
    const obj = response as Record<string, unknown>;
    if (typeof obj.guestId === "string") {
      return [obj.guestId];
    }
  }
  return [];
}

// Create multiple guests in batch
export async function createGuestsBatch(
  eventId: string,
  guests: Guest[],
): Promise<string[]> {
  const response = await apiPost<string>(
    `${BASE_PATH}${eventId}/batch`,
    guests,
    {
      "Cache-Control": "no-store",
    },
  );

  return parseGuestBatchResponse(response);
}

// Get a guest by ID
export async function getGuest(
  eventId: string,
  guestId: string,
  fields?: string,
): Promise<Guest> {
  const queryParams = fields ? `?fields=${encodeURIComponent(fields)}` : "";
  return await apiGet<Guest>(
    `${BASE_PATH}${eventId}/${guestId}${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Fetch the signed scan token for a guest. Returns `<guestIdHex>.<sig>` —
 * what the PGuestQR modal encodes into the rendered QR. Signing happens on
 * the backend so the per-event HMAC key never reaches the browser.
 * No-store on the response: each modal open hits the server.
 */
export async function fetchGuestQrToken(
  eventId: string,
  guestId: string,
): Promise<string> {
  const { token } = await apiGet<{ token: string }>(
    `${BASE_PATH}${eventId}/${guestId}/qr-token`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  return token;
}

/**
 * List the event's durable CSV import batches, newest first. Powers the guest
 * list "Import" (source) filter: each batch becomes a picker option that emits
 * an `importId = <hex>` filter.
 */
export async function getGuestImports(
  eventId: string,
): Promise<ImportBatch[]> {
  return await apiGet<ImportBatch[]>(
    `${BASE_PATH}${eventId}/imports`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * What the batch update actually announced, per guest id.
 *
 * Present only when the write derived a lifecycle trigger — a re-decision with
 * the opt-in, in practice. An ordinary edit answers with an empty body, so this
 * is `undefined` almost always, and the caller must treat "no body" and "no
 * triggers" as the same thing.
 *
 * "Enqueued", not "delivered": the server promises the envelope reached the
 * queue, nothing beyond that. That is still strictly more than the cockpit knew
 * before, which was nothing at all — it reported success from its own checkbox.
 */
export type BatchUpdateAck = {
  lifecycleTriggers?: Record<string, string[]>;
};

export async function updateGuests(
  eventId: string,
  guests: Guest[],
): Promise<BatchUpdateAck | void> {
  return apiPut<BatchUpdateAck | void>(
    `${BASE_PATH}${eventId}/update-batch`,
    guests.map((guest) => ({
      guestType: guest.guestType,
      mainGuest: guest.mainGuest,
      guestId: guest.guestId,
      state: guest.state,
      gender: guest.gender,
      title: guest.title,
      acceptanceState: guest.acceptanceState,
      firstName: guest?.firstName,
      lastName: guest.lastName,
      company: guest.company,
      jobTitle: guest.jobTitle,
      address: guest.address,
      address2: guest.address2,
      zipCode: guest.zipCode,
      city: guest.city,
      country: guest.country,
      email: guest.email,
      phone: guest.phone,
      mobile: guest.mobile,
      language: guest.language,
      username: guest.username,
      code: guest.code,
      archived: guest.archived,
      emailOptIn: guest.emailOptIn,
      // SMS channel wave: the SMS consent rides the same explicit copy; the
      // backend strips it while its switch is off.
      smsOptIn: guest.smsOptIn,
      // Never a name the backend refuses to unset on purpose (smsOptIn —
      // strict consent; guestClearFields.ts mirrors CLEARABLE_FIELDS).
      clearFields: withoutNonClearable(guest.clearFields),
      additionalFields: guest.additionalFields,
      overrideRules: guest.overrideRules,
      forceWaitlist: guest.forceWaitlist,
      retriggerAutomations: guest.retriggerAutomations,
    })),
    {
      "Cache-Control": "no-store",
    },
    // Explicit: without it an empty 200 comes back as the raw `""` axios hands
    // over, and the caller has to know that to tell it from an object.
    true,
  );
}

export async function checkInOut(
  eventId: string,
  guestId: string,
  checkIn: boolean,
  performedByType: string = "Admin Suite",
): Promise<{ warning?: string } | void> {
  const text = await apiPut<string>(
    `${BASE_PATH}${eventId}/${guestId}/check-in-out`,
    {
      checkIn,
      performedByType,
    },
    {
      "Cache-Control": "no-store",
    },
  );
  if (text) {
    try {
      return JSON.parse(text) as { warning?: string };
    } catch {
      // empty or non-JSON response = success with no warning
    }
  }
}

export async function batchCheckInOut(
  eventId: string,
  checkIn: boolean,
  filters: Filter[],
  performedByType: string = "Admin Suite",
): Promise<{ processed: number; skipped?: number; capacityReached?: boolean }> {
  return apiPut<{ processed: number; skipped?: number; capacityReached?: boolean }>(
    `${BASE_PATH}${eventId}/batch-check-in-out`,
    {
      checkIn,
      performedByType,
      filters,
    },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function getReviewedGuestCount(
  eventId: string,
  filters: Filter[],
  archived: boolean,
  searchTerm: string,
): Promise<number> {
  const params = new URLSearchParams({
    archived: archived.toString(),
    filters: JSON.stringify(filters),
  });

  // Only add searchTerm if it's not empty
  if (searchTerm && searchTerm.trim() !== "") {
    params.append("searchTerm", searchTerm);
  }

  return await apiGet<number>(
    `${BASE_PATH}${eventId}/reviewed-guest-count?${params.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * Gets the count of companions matching the specified criteria
 * @param eventId The ID of the event
 * @param archived Whether to count archived companions (default: false)
 * @returns A promise that resolves to the count of matching companions
 */
export async function countCompanions(
  eventId: string,
  archived: boolean = false,
  filters: Filter[] = [],
  searchTerm: string = "",
): Promise<number> {
  // searchTerm only when non-empty — keeps no-search URLs byte-identical
  // (mirrors getReviewedGuestCount; also safe against a pre-deploy backend).
  const searchParam =
    searchTerm && searchTerm.trim() !== ""
      ? `&searchTerm=${encodeURIComponent(searchTerm)}`
      : "";
  const queryParams = `?archived=${archived}&filters=${encodeURIComponent(
    JSON.stringify(filters),
  )}${searchParam}`;

  return await apiGet<number>(
    `${BASE_PATH}${eventId}/count-companions${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function getOrderedCompanionsWithGuests(
  eventId: string,
  archived: boolean = false,
  guestFields: string = "firstName,lastName,email,company",
  filters: Filter[] = [],
  page: number = 0,
  size: number = 10,
  searchTerm: string = "",
): Promise<Guest[]> {
  // searchTerm only when non-empty — see countCompanions.
  const searchParam =
    searchTerm && searchTerm.trim() !== ""
      ? `&searchTerm=${encodeURIComponent(searchTerm)}`
      : "";
  const queryParams = `?archived=${archived}&fields=${encodeURIComponent(
    guestFields,
  )}&page=${page}&size=${size}&filters=${encodeURIComponent(
    JSON.stringify(filters),
  )}${searchParam}`;

  return await apiGet<Guest[]>(
    `${BASE_PATH}${eventId}/ordered-with-guests${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function getValidGuestCount(
  eventId: string,
  filters: Filter[],
  archived: boolean,
  searchTerm: string = "",
  whitelist?: string[],
  blacklist?: string[],
  requireEmail?: boolean,
  // Nested-conditions mode (dual-support): backend ignores `filters` when set.
  conditions?: ConditionGroup,
  // Party counting: with `groupBy: "mainGuest"` the answer is the number of
  // PARTIES, resolved by the same code the grouped listing pages with. The
  // guest list asks for both numbers in party mode — parties drive pagination,
  // people drive the selection banner.
  groupBy?: string,
): Promise<number> {
  const requestBody = {
    archived,
    filters: conditions ? [] : filters,
    searchTerm,
    whitelist: whitelist ?? [],
    blacklist: blacklist ?? [],
    ...(requireEmail && { requireEmail }),
    ...(conditions && { conditions }),
    ...(groupBy ? { groupBy } : {}),
  };

  return await apiPost<number>(
    `${BASE_PATH}${eventId}/valid-guest-count`,
    requestBody,
    {
      "Cache-Control": "no-store",
      "Content-Type": "application/json",
    },
    true,
  );
}

export type GuestGroupCount = { value: string; count: number };

/**
 * Row counts per value of one grouped column — the number on a group header.
 *
 * Its own endpoint rather than the analytics `count-guests`: that one applies
 * the dashboard's PII gates, knows nothing about this list's search term or
 * selection lists, and narrows differently per key. A header number from a
 * different predicate than the rows under it is worse than no number, so the
 * caller renders headers bare when this fails or answers empty.
 */
export async function getGuestGroupCounts(
  eventId: string,
  filters: Filter[],
  archived: boolean,
  groupBy: string,
  searchTerm: string = "",
  conditions?: ConditionGroup,
): Promise<GuestGroupCount[]> {
  const requestBody = {
    archived,
    filters: conditions ? [] : filters,
    searchTerm,
    groupBy,
    ...(conditions && { conditions }),
  };

  return await apiPost<GuestGroupCount[]>(
    `${BASE_PATH}${eventId}/group-counts`,
    requestBody,
    {
      "Cache-Control": "no-store",
      "Content-Type": "application/json",
    },
    true,
  );
}

/**
 * Live match count for a nested ConditionGroup — powers the "N guests match"
 * footer in condition editors. Hits the same `count-guests` endpoint the AI
 * `count_guests` tool uses; the nested `conditions` body variant is the
 * dual-support path (flat `filters` stays for legacy/AI callers). Response is
 * the endpoint's GuestCountResult envelope; only `total` matters here.
 */
export async function countGuestsByConditions(
  eventId: string,
  conditions: ConditionGroup,
): Promise<number> {
  const result = await apiPost<{ total: number }>(
    `${BASE_PATH}${eventId}/count-guests`,
    { conditions },
    {
      "Cache-Control": "no-store",
      "Content-Type": "application/json",
    },
    true,
  );
  return result.total;
}

/**
 * Consent breakdown over the same selection descriptor a marketing send
 * targets, for the pre-send opt-in warning. Email-gated server-side.
 */
export async function getEmailOptInBreakdown(
  eventId: string,
  filters: Filter[],
  archived: boolean,
  searchTerm: string = "",
  whitelist?: string[],
  blacklist?: string[],
  // Conditions lane (mirrors getValidGuestCount): non-empty tree REPLACES
  // the flat filters server-side — callers pass filters: [] alongside it.
  conditions?: ConditionGroup,
): Promise<EmailOptInBreakdown> {
  const requestBody = {
    archived,
    filters: conditions ? [] : filters,
    searchTerm,
    whitelist: whitelist ?? [],
    blacklist: blacklist ?? [],
    ...(conditions && { conditions }),
  };

  return await apiPost<EmailOptInBreakdown>(
    `${BASE_PATH}${eventId}/email-optin-breakdown`,
    requestBody,
    {
      "Cache-Control": "no-store",
      "Content-Type": "application/json",
    },
    true,
  );
}

/**
 * Delete multiple guests in a batch operation using query parameters
 * @param eventId The ID of the event containing the guests
 * @param guestIds Array of guest IDs to delete
 * @returns A promise that resolves when the deletion is complete
 */
export async function deleteGuestsBatch(
  eventId: string,
  guestIds: string[],
  options?: { permanent?: boolean },
): Promise<DeleteGuestsResult | null> {
  // Convert the array to a comma-separated string
  const idsString = guestIds.join(",");
  // W6: the legacy door trashes too; `permanent=true` is today's hard delete.
  const permanentQs = options?.permanent ? "&permanent=true" : "";
  const url = `${BASE_PATH}${eventId}/batch-delete?ids=${encodeURIComponent(
    idsString,
  )}${permanentQs}`;

  const result = await apiDelete<unknown>(url, {
    "Cache-Control": "no-store",
  });
  return readDeleteGuestsResult(result);
}

/**
 * Filter-aware bulk delete. Provide either `ids` (selected rows) OR `filters`
 * (select-all-matching). Server resolves IDs when `filters` is present.
 *
 * P3 (BG6): uses DELETE + `/batch-delete-by-criteria` — the backend
 * disambiguated from the sibling `DELETE /batch-delete?ids=…` path and
 * corrected the verb to match semantics.
 */
export async function deleteGuestsBulk(
  eventId: string,
  body: {
    ids?: string[];
    filters?: Filter[];
    excludeIds?: string[];
    search?: string;
    archived?: boolean;
    // Explicit "select-all-in-scope" — mirrors the backend's BulkDeleteRequest.selectAll.
    // Set when the FE's all-matching banner is active so an empty `filters` body is legal.
    selectAll?: boolean;
    /**
     * W6 guest trash: absent/false = the backend decides (TRASH while the flag
     * is on, today's hard delete while it is off); true = skip the trash.
     * Ignored by a backend without the trash.
     */
    permanent?: boolean;
  },
): Promise<DeleteGuestsResult | null> {
  // `{mode, count, trashBatchId?, purgeAt?}` from a trash-aware backend; an
  // empty body (204) from the one before it — `null` here, and the callers
  // read null as "nothing to undo".
  const result = await apiDelete<unknown>(
    `${BASE_PATH}${eventId}/batch-delete-by-criteria`,
    {
      "Cache-Control": "no-store",
    },
    body,
  );
  return readDeleteGuestsResult(result);
}

/**
 * Read-only preview for the delete-confirmation dialog: same body as
 * `deleteGuestsBulk`, but resolves the selection + companion cascade and
 * returns counts (cascaded companions, open requests, checked-in) WITHOUT
 * deleting anything.
 */
export async function getGuestDeleteImpact(
  eventId: string,
  body: {
    ids?: string[];
    filters?: Filter[];
    excludeIds?: string[];
    search?: string;
    archived?: boolean;
    selectAll?: boolean;
  },
): Promise<import("@/types/GuestDeleteImpact").GuestDeleteImpact> {
  return await apiPost<import("@/types/GuestDeleteImpact").GuestDeleteImpact>(
    `${BASE_PATH}${eventId}/delete-impact`,
    body,
    { "Cache-Control": "no-store" },
  );
}

/**
 * Filter-aware bulk update. Resolves matching IDs server-side and applies the
 * whitelisted field updates. The `updates` whitelist mirrors the backend's
 * `BulkUpdateFields` struct — extending one side requires extending the other.
 */
export async function updateGuestsByFilter(
  eventId: string,
  body: {
    filters: Filter[];
    excludeIds?: string[];
    search?: string;
    archived?: boolean;
    // Mirrors BulkUpdateByFilterRequest.selectAll — when true, an empty `filters`
    // list is legal and resolves to every guest in scope.
    selectAll?: boolean;
    updates: {
      archived?: boolean;
      state?: string;
      gender?: string;
      language?: string;
      emailOptIn?: boolean | null;
      clearFields?: string[];
      additionalFields?: Record<string, unknown>;
    };
  },
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventId}/batch-update-by-filter`,
    body,
    {
      "Cache-Control": "no-store",
    },
  );
}

// Delete a guest
export async function deleteGuest(
  eventId: string,
  guestId: string,
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/${guestId}`, {
    "Cache-Control": "no-store",
  });
}

/**
 * Lift the bounced-address suppression on one guest (PB-TH-7).
 *
 * A hard bounce marks the mailbox undeliverable, and that check runs BEFORE
 * the consent gate in the sender — so while it stands the guest receives
 * nothing at all, tickets and confirmations included. This is the only way to
 * clear it; there is deliberately no call that sets it, because only a real
 * bounce may decide an address is dead.
 *
 * `cleared:false` is a success, not an error: the guest was already
 * deliverable, so whatever mail they are missing has another cause.
 */
export async function clearEmailUndeliverable(
  eventId: string,
  guestId: string,
): Promise<{ cleared: boolean; pending?: boolean; reason?: string }> {
  // `pending: true` (HTTP 202) is the normal answer: the release of the address
  // from the account-level SES suppression list is with the Worker, and the
  // guest's flag stays up until that actually succeeds. `cleared: true` is now
  // only the no-address case, where there is nothing to release.
  return apiDelete<{ cleared: boolean; pending?: boolean; reason?: string }>(
    `${BASE_PATH}${eventId}/${guestId}/email-undeliverable`,
    { "Cache-Control": "no-store" },
    undefined,
    true,
  );
}

/**
 * Find duplicate guests by a composite key with pagination. The backend
 * groups by ALL listed fields (`field=a,b,…`, 1..5 built-in fields) and
 * returns the members of every group with more than one guest, ordered so
 * that a group's members are adjacent.
 * @param eventId The ID of the event
 * @param fieldNames The fields that together form the key (e.g. ['firstName','lastName'])
 * @param fields Comma-separated list of fields to return (default: 'guestId,firstName,lastName,email')
 * @param page Page number for pagination (default: 0)
 * @param pageSize Number of items per page (default: 10)
 * @returns A promise that resolves to duplicates and total count
 */
export async function findDuplicatesByField(
  eventId: string,
  fieldNames: readonly string[],
  fields: string = "guestId,firstName,lastName,email",
  page: number = 0,
  pageSize: number = 10,
): Promise<DuplicatesResponse> {
  const queryParams = `?field=${encodeURIComponent(
    fieldNames.join(","),
  )}&fields=${encodeURIComponent(fields)}&page=${encodeURIComponent(
    page,
  )}&pageSize=${encodeURIComponent(pageSize)}`;

  return await apiGet<DuplicatesResponse>(
    `${BASE_PATH}${eventId}/duplicates${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * One matched duplicate row. `matchedGuestId`/display fields identify the
 * existing guest the row collides with (for the "overwrite that guest" action
 * and the side-by-side preview). For an intra-batch match — the row duplicates
 * an EARLIER row of the same file — `intraBatch` is true, `matchedGuestId` is
 * null, and the display fields are null (the FE renders "duplicate within file").
 */
export interface DedupeMatch {
  rowIndex: number;
  matchedGuestId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  intraBatch: boolean;
  /**
   * Contact-pool preview only (POST /contacts/import/dedupe-preview): the id
   * of the matched existing CONTACT. Absent/undefined on guest previews —
   * additive so guest callers are untouched.
   */
  matchedContactId?: string | null;
  /**
   * Contact-pool preview only: true when the KEEP action is unavailable for
   * this row because the incoming row's normalized email equals the matched
   * contact's (the unique {companyId, emailNormalized} index forbids a second
   * contact with the same email — KEEP is remapped to SKIP server-side). The
   * FE shows "cannot keep both: same email" copy. Absent on guest previews.
   */
  keepUnavailable?: boolean;
}

export interface DedupePreviewResponse {
  duplicateRowIndexes: number[];
  duplicateCount: number;
  duplicates: DedupeMatch[];
}

/**
 * Preview which incoming import rows would be skipped as duplicates of an
 * existing active guest under a `matches_new` dedupe rule. `rows` carry only the
 * fields the rule references, keyed exactly by the condition keys (e.g.
 * `email`, `additionalFields.tier`). Read-only — no guests are created.
 */
export async function previewImportDuplicates(
  eventId: string,
  conditions: unknown,
  rows: Record<string, unknown>[],
): Promise<DedupePreviewResponse> {
  return await apiPost<DedupePreviewResponse>(
    `${BASE_PATH}${eventId}/import/dedupe-preview`,
    { conditions, rows },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Get paginated guests
export async function getPaginatedGuests(
  fields: string,
  searchTerm: string,
  page: number,
  pageSize: number,
  filter: Filter[] = [],
  sortBy: string = "createdAt",
  sortOrder: "asc" | "desc" = "desc",
  eventId: string,
  whitelist?: string[],
  blacklist?: string[],
  // Nested-conditions mode (dual-support): when `conditions` is set the
  // backend ignores `filters` (sent empty) and `archived` travels as a
  // request-level boolean instead of a flat filter row.
  // `groupBy` is the guest list's row grouping: "mainGuest" pages PARTIES
  // (companions ride inside each row's `companions`), a field key keeps rows
  // as people but orders equal values together. Omitted = today's flat list;
  // the key is left off the body entirely so an ungrouped request stays
  // byte-identical to what shipped before the feature.
  opts?: { conditions?: ConditionGroup; archived?: boolean; groupBy?: string },
): Promise<Guest[]> {
  const requestBody = {
    page,
    pageSize,
    fields,
    searchTerm,
    filters: opts?.conditions ? [] : filter,
    sortBy,
    sortOrder,
    whitelist: whitelist ?? [],
    blacklist: blacklist ?? [],
    ...(opts?.groupBy ? { groupBy: opts.groupBy } : {}),
    ...(opts?.conditions && {
      conditions: opts.conditions,
      archived: opts.archived ?? false,
    }),
  };

  return await apiPost<Guest[]>(
    `${BASE_PATH}event/${eventId}/paginated`,
    requestBody,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Get paginated guests inversed (returns guests that DON'T match the criteria)
export async function getPaginatedGuestsInversed(
  fields: string,
  searchTerm: string,
  page: number,
  pageSize: number,
  filter: Filter[] = [],
  sortBy: string = "createdAt",
  sortOrder: "asc" | "desc" = "desc",
  eventId: string,
  whitelist?: string[],
  blacklist?: string[],
  // `allRecipients` names WHICH selection the backend complements: `true` =
  // the all-matching set (filters/conditions minus blacklist), `false` = the
  // explicit whitelist, so the response is "everyone not yet picked". Omitted
  // means `true` server-side. Conditions mode mirrors getPaginatedGuests: when
  // `conditions` is set the backend ignores `filters` (sent empty) and
  // `archived` travels as a request-level boolean.
  opts?: {
    conditions?: ConditionGroup;
    archived?: boolean;
    allRecipients?: boolean;
  },
): Promise<Guest[]> {
  const requestBody = {
    page,
    pageSize,
    fields,
    searchTerm,
    filters: opts?.conditions ? [] : filter,
    sortBy,
    sortOrder,
    whitelist: whitelist ?? [],
    blacklist: blacklist ?? [],
    archived: opts?.archived ?? false,
    ...(opts?.conditions && { conditions: opts.conditions }),
    ...(opts?.allRecipients !== undefined && {
      allRecipients: opts.allRecipients,
    }),
  };

  const t = await apiPost<Guest[]>(
    `${BASE_PATH}event/${eventId}/paginated-inversed`,
    requestBody,
    {
      "Cache-Control": "no-store",
    },
    true,
  );

  return t;
}

// Get paginated guests to be reviewed
export async function getPaginatedGuestsToBeReviewed(
  eventId: string,
  page: number = 1,
  pageSize: number = 10,
  filter: Filter[] = [],
  sortBy: string = "createdAt",
  sortOrder: "asc" | "desc" = "desc",
  fields: string,
  archived: boolean = false,
  searchTerm: string = "",
): Promise<Guest[]> {
  // searchTerm only when non-empty — see countCompanions.
  const searchParam =
    searchTerm && searchTerm.trim() !== ""
      ? `&searchTerm=${encodeURIComponent(searchTerm)}`
      : "";
  const queryParams = `?page=${encodeURIComponent(
    page,
  )}&pageSize=${encodeURIComponent(pageSize)}&fields=${encodeURIComponent(
    fields,
  )}&filters=${encodeURIComponent(
    JSON.stringify(filter),
  )}&sortBy=${encodeURIComponent(sortBy)}&sortOrder=${encodeURIComponent(
    sortOrder,
  )}&archived=${archived}${searchParam}`;

  const t = await apiGet<Guest[]>(
    `${BASE_PATH}event/${eventId}/to-be-reviewed${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );

  return t;
}

/**
 * Get specific guests by their IDs with optional filtering and pagination
 * @param eventId The ID of the event
 * @param guestIds Array of guest IDs to retrieve
 * @param searchTerm Optional search term to filter results
 * @param fields Comma-separated list of fields to return (default: 'guestId,firstName,lastName,email')
 * @param filters Array of filters to apply
 * @param page Page number for pagination (default: 0)
 * @param pageSize Number of items per page (default: 10)
 * @returns A promise that resolves to an array of matching guests
 */
export async function getGuestsByIds(
  eventId: string,
  guestIds: string[],
  searchTerm: string = "",
  fields: string = "guestId,firstName,lastName,email",
  filters: Filter[] = [],
  page: number = 0,
  pageSize: number = 10,
): Promise<Guest[]> {
  const requestBody = {
    guestIds,
    searchTerm,
    fields,
    filters,
    page,
    pageSize,
  };

  return await apiPost<Guest[]>(
    `${BASE_PATH}${eventId}/guests-by-id`,
    requestBody,
    {
      "Cache-Control": "no-store",
      "Content-Type": "application/json",
    },
    true,
  );
}

// Get companions for a specific guest (main guest)
export async function getCompanions(
  eventId: string,
  guestId: string,
  fields?: string,
  /**
   * WHICH companions. Omitted = the set this route has always answered
   * ("anything but DECLINED", archived rows included) — two callers depend on
   * exactly that and say so at their own call site. See `CompanionScope`.
   */
  scope?: CompanionScope,
): Promise<Guest[]> {
  const params = new URLSearchParams();
  if (fields) params.set("fields", fields);
  if (scope) params.set("scope", scope);
  const queryParams = params.toString() ? `?${params.toString()}` : "";
  return await apiGet<Guest[]>(
    `${BASE_PATH}${eventId}/${guestId}/companions${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function createPending(
  eventId: string,
  guest: Guest,
): Promise<{ guestIds: string[]; guest?: Guest; outcome?: string }> {
  const response = await apiPost<string>(
    `${BASE_PATH}${eventId}/create-pending`,
    guest,
    {
      "Cache-Control": "no-store",
      "Content-Type": "application/json",
    },
    true,
  );

  // The backend has always returned `{outcome, guestId, guest}` here
  // (GuestResource.createPendingAfterValidation), but this client reduced it to
  // the id and threw the hydrated DTO away — so the optimistic row was the raw
  // FORM object, missing acceptanceState/createdAt/state/username and the
  // server-applied additionalField defaults. That was survivable while the row
  // lived only in provider state; it is not, now that it is written into the
  // query snapshot the next mount re-seeds from. Mirrors createCompanionRequest.
  const body =
    response && typeof response === "object" && !Array.isArray(response)
      ? (response as { guest?: Guest; outcome?: string })
      : undefined;

  return {
    guestIds: parseGuestBatchResponse(response),
    guest: body?.guest,
    outcome: body?.outcome,
  };
}

export async function createCompanionRequest(
  eventId: string,
  guest: Guest,
): Promise<{ guestId: string; guest?: Guest }> {
  // P3 (UX9): backend now returns a hydrated GuestDTO on
  // create-companion-request alongside the guestId. The legacy
  // string-only shape is no longer produced by this endpoint —
  // consume the object directly instead of routing through
  // parseGuestBatchResponse (which dropped the guest DTO).
  const response = await apiPost<string>(
    `${BASE_PATH}${eventId}/create-companion-request`,
    guest,
    {
      "Cache-Control": "no-store",
      "Content-Type": "application/json",
    },
  );
  const body = typeof response === "string" ? JSON.parse(response) : response;
  return {
    guestId: String((body as { guestId: unknown }).guestId ?? ""),
    guest: (body as { guest?: Guest }).guest,
  };
}
